import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Center,
  Code,
  Divider,
  Group,
  Loader,
  NumberInput,
  Paper,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconArrowLeft, IconPrinter } from '@tabler/icons-react';
import { PageHeader } from '../../shared/components/PageHeader';
import { ApiError } from '../../shared/api/problem';
import {
  fetchPackingList,
  generatePackingList,
  type PackingListLineDto,
} from './api';

/**
 * The packing list — the document that goes in the box.
 *
 * **This is the whole of legacy's packing user interface**, rebuilt. The Order Ship grid there has
 * one Pack List button: it asks "Please enter the number of Cartons" and downloads a PDF. There is
 * no browse screen — sp_WebPackingList_GetAll and _GetMostRecent exist and nothing calls either —
 * so there is none here.
 *
 * **It prints from the browser rather than from the server.** legacy renders through DevExpress
 * XtraReports; adding a server-side reporting stack for one document would be a large dependency
 * bought for a single page, and a PWA already has a print dialog that writes PDF. The trade is that
 * the paper is styled by the CSS below instead of by a report designer.
 *
 * **The numbers are frozen and say so.** Everything except the carton count was copied at ship
 * confirm, so a customer address corrected next week does not rewrite a document already sent. The
 * note at the foot exists because somebody WILL notice the addresses disagreeing with the customer
 * record and file it as a bug.
 */
export function PackingListRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams({ from: '/app/picks/$id/packing-list' });
  const queryClient = useQueryClient();

  const [cartons, setCartons] = useState<number | ''>('');

  const query = useQuery({
    queryKey: ['packing-list', id] as const,
    queryFn: ({ signal }) => fetchPackingList(id, signal),
  });

  const lines = useMemo(() => query.data ?? [], [query.data]);
  // The shipment fields repeat on every row, so the header is row one. That is what the API
  // returns and what legacy's report binds to.
  const head = lines[0];

  useEffect(() => {
    setCartons(head?.cartonCount ?? '');
  }, [head?.cartonCount]);

  const mutation = useMutation({
    mutationFn: () =>
      generatePackingList(id, {
        cartonCount: cartons === '' ? null : Number(cartons),
      }),
    onSuccess: (rows) => {
      queryClient.setQueryData(['packing-list', id], rows);
      notifications.show({
        color: 'teal',
        title: t('picks.packing.savedTitle'),
        message: t('picks.packing.savedBody'),
      });
    },
  });

  const totalShipped = lines.reduce((sum, l) => sum + l.quantityShipped, 0);
  const anyBackOrdered = lines.some((l) => l.quantityBackOrdered > 0);

  return (
    <>
      {/*
        Printing hides the shell rather than opening a second window. `visibility` and not
        `display`, because collapsing the app frame to nothing reflows the document being printed
        and drops the page break with it.
      */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #packing-list, #packing-list * { visibility: visible; }
          #packing-list {
            position: absolute;
            inset: 0;
            margin: 0;
            padding: 0;
            width: 100%;
            border: none;
            box-shadow: none;
            background: #fff;
            color: #000;
          }
          .packing-list-noprint { display: none !important; }
        }
      `}</style>

      <PageHeader title={t('picks.packing.title')} />

      <Stack gap="md">
        <Group className="packing-list-noprint" gap="sm" wrap="wrap">
          <Button
            variant="default"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => void navigate({ to: '/app/picks/$id', params: { id } })}
          >
            {t('picks.packing.backToPick')}
          </Button>
        </Group>

        {query.isLoading && (
          <Center py="xl">
            <Loader color="invetrak" />
          </Center>
        )}

        {query.isError && (
          <Alert
            className="packing-list-noprint"
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
            title={t('picks.packing.loadFailed')}
          >
            <Stack gap="xs">
              <Text size="sm">{(query.error as Error).message}</Text>
              {query.error instanceof ApiError && query.error.problem.correlationId && (
                <Text size="xs" c="dimmed">
                  {t('errors.genericBody')} <Code>{query.error.problem.correlationId}</Code>
                </Text>
              )}
              <Group>
                <Button size="xs" variant="default" onClick={() => void query.refetch()}>
                  {t('picks.retry')}
                </Button>
              </Group>
            </Stack>
          </Alert>
        )}

        {/*
          Not an error. A pick that has not shipped has no document yet, and one that shipped before
          tenant script 072 landed has none either — the button below makes it in both cases, and
          the server refuses the first with a message of its own.
        */}
        {query.isSuccess && lines.length === 0 && (
          <Alert className="packing-list-noprint" color="gray" variant="light">
            <Stack gap="sm" align="flex-start">
              <Text size="sm">{t('picks.packing.notIssued')}</Text>
              <Button
                color="invetrak"
                loading={mutation.isPending}
                onClick={() => mutation.mutate()}
              >
                {t('picks.packing.generate')}
              </Button>
            </Stack>
          </Alert>
        )}

        {mutation.isError && (
          <Alert
            className="packing-list-noprint"
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
            title={t('picks.packing.saveFailed')}
          >
            {(mutation.error as Error).message}
          </Alert>
        )}

        {query.isSuccess && head && (
          <>
            <Group className="packing-list-noprint" gap="sm" align="flex-end" wrap="wrap">
              <NumberInput
                label={t('picks.packing.cartons')}
                description={t('picks.packing.cartonsHelp')}
                value={cartons}
                onChange={(v) => setCartons(v === '' ? '' : Number(v))}
                min={0}
                allowDecimal={false}
                w={{ base: '100%', sm: 180 }}
              />
              <Button
                variant="default"
                loading={mutation.isPending}
                onClick={() => mutation.mutate()}
              >
                {t('picks.packing.saveCartons')}
              </Button>
              <Button
                color="invetrak"
                leftSection={<IconPrinter size={16} />}
                onClick={() => window.print()}
              >
                {t('picks.packing.print')}
              </Button>
            </Group>

            <Paper id="packing-list" withBorder p="lg" radius="md">
              <Stack gap="md">
                <Group justify="space-between" align="flex-start" wrap="wrap">
                  <Stack gap={2}>
                    <Title order={3}>{t('picks.packing.documentTitle')}</Title>
                    <Text size="sm">
                      {t('picks.packing.shipmentNumber')}: <strong>{head.pickNumber}</strong>
                    </Text>
                    {head.orderNumber && (
                      <Text size="sm">
                        {t('picks.packing.orderNumber')}: <strong>{head.orderNumber}</strong>
                      </Text>
                    )}
                    {head.customerPo && (
                      <Text size="sm">
                        {t('picks.packing.customerPo')}: <strong>{head.customerPo}</strong>
                      </Text>
                    )}
                  </Stack>

                  <Stack gap={2} align="flex-end">
                    {head.shippedAtUtc && (
                      <Text size="sm">
                        {t('picks.packing.shipped')}:{' '}
                        <strong>{new Date(head.shippedAtUtc).toLocaleDateString()}</strong>
                      </Text>
                    )}
                    {head.shipMethodName && (
                      <Text size="sm">
                        {t('picks.packing.carrier')}: <strong>{head.shipMethodName}</strong>
                      </Text>
                    )}
                    {head.trackingNumber && (
                      <Text size="sm">
                        {t('picks.packing.tracking')}: <strong>{head.trackingNumber}</strong>
                      </Text>
                    )}
                    {head.billOfLading && (
                      <Text size="sm">
                        {t('picks.packing.bol')}: <strong>{head.billOfLading}</strong>
                      </Text>
                    )}
                    <Text size="sm">
                      {t('picks.packing.cartons')}:{' '}
                      <strong>{head.cartonCount ?? t('picks.packing.cartonsUnknown')}</strong>
                    </Text>
                  </Stack>
                </Group>

                <Divider />

                <Group align="flex-start" gap="xl" wrap="wrap">
                  <AddressBlock
                    heading={t('picks.packing.shipFrom')}
                    name={head.shipFromName}
                    line1={head.shipFromAddressLine1}
                    line2={head.shipFromAddressLine2}
                    city={head.shipFromCity}
                    state={head.shipFromState}
                    postalCode={head.shipFromPostalCode}
                  />
                  <AddressBlock
                    heading={t('picks.packing.shipTo')}
                    name={head.shipToName}
                    line1={head.shipToAddressLine1}
                    line2={head.shipToAddressLine2}
                    city={head.shipToCity}
                    state={head.shipToState}
                    postalCode={head.shipToPostalCode}
                  />
                </Group>

                <Table striped withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{t('picks.packing.item')}</Table.Th>
                      <Table.Th>{t('picks.packing.description')}</Table.Th>
                      <Table.Th>{t('picks.packing.lotSerial')}</Table.Th>
                      <Table.Th>{t('picks.packing.uom')}</Table.Th>
                      <Table.Th ta="right">{t('picks.packing.ordered')}</Table.Th>
                      <Table.Th ta="right">{t('picks.packing.thisShipment')}</Table.Th>
                      <Table.Th ta="right">{t('picks.packing.backOrdered')}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {lines.map((line) => (
                      <Table.Tr key={line.packingListLineId}>
                        <Table.Td>
                          <Code>{line.itemNumber}</Code>
                        </Table.Td>
                        <Table.Td>{line.itemDescription ?? '—'}</Table.Td>
                        <Table.Td>{lotSerial(line)}</Table.Td>
                        <Table.Td>{line.uomCode ?? '—'}</Table.Td>
                        <Table.Td ta="right">{line.quantityOrdered}</Table.Td>
                        <Table.Td ta="right">
                          <strong>{line.quantityShipped}</strong>
                        </Table.Td>
                        <Table.Td ta="right">{line.quantityBackOrdered}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>

                {/*
                  A total across lines in DIFFERENT units would add cases to eaches, so this is
                  shown only when every line shares one unit. Everywhere else in this application
                  the answer was to show no total at all; here the document wants one and a
                  single-unit shipment is the common case.
                */}
                {sharedUom(lines) && (
                  <Group justify="flex-end">
                    <Text size="sm">
                      {t('picks.packing.totalShipped')}:{' '}
                      <strong>
                        {totalShipped} {sharedUom(lines)}
                      </strong>
                    </Text>
                  </Group>
                )}

                {anyBackOrdered && (
                  <Text size="sm" fs="italic">
                    {t('picks.packing.backOrderNote')}
                  </Text>
                )}

                <Text size="xs" c="dimmed">
                  {t('picks.packing.frozenNote', {
                    when: new Date(head.createdAtUtc).toLocaleString(),
                  })}
                </Text>
              </Stack>
            </Paper>
          </>
        )}
      </Stack>
    </>
  );
}

/** Lot and serial in one cell: a line carries at most one of each, and usually neither. */
function lotSerial(line: PackingListLineDto): string {
  const parts = [line.lotNumber, line.serialNumber].filter(Boolean);
  return parts.length > 0 ? parts.join(' / ') : '—';
}

/** The unit every line shares, or null when they differ and no total can be honestly shown. */
function sharedUom(lines: PackingListLineDto[]): string | null {
  if (lines.length === 0) return null;
  const first = lines[0].uomCode;
  if (!first) return null;
  return lines.every((l) => l.uomCode === first) ? first : null;
}

interface AddressProps {
  heading: string;
  name: string | null;
  line1: string | null;
  line2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
}

function AddressBlock({ heading, name, line1, line2, city, state, postalCode }: AddressProps) {
  const region = [city, state, postalCode].filter(Boolean).join(' ');
  return (
    <Stack gap={2} miw={220}>
      <Text size="xs" fw={700} tt="uppercase" c="dimmed">
        {heading}
      </Text>
      {name && <Text size="sm">{name}</Text>}
      {line1 && <Text size="sm">{line1}</Text>}
      {line2 && <Text size="sm">{line2}</Text>}
      {region && <Text size="sm">{region}</Text>}
    </Stack>
  );
}
